import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Phone, Mail, Instagram, MessageCircle, Loader2 } from "lucide-react";
import { HOTEL } from "../../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  checkIn: "",
  checkOut: "",
  guests: "2",
  message: "",
};

export const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please add your name and email so we can reply.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings`, form);
      toast.success("Thank you! Your request has been sent. We'll be in touch soon.");
      setForm(initialForm);
    } catch (err) {
      toast.error("Something went wrong. Please try WhatsApp or email instead.");
    } finally {
      setSubmitting(false);
    }
  };

  const contactItems = [
    { icon: MapPin, key: "location", label: HOTEL.location, href: null },
    { icon: Phone, key: "phone", label: HOTEL.phone, href: `tel:${HOTEL.phoneDigits}` },
    { icon: Mail, key: "email", label: HOTEL.email, href: `mailto:${HOTEL.email}` },
    { icon: Instagram, key: "instagram", label: `@${HOTEL.instagram}`, href: HOTEL.instagramUrl },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-sand-100 py-24 sm:py-32"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-xs tracking-[0.25em] uppercase text-earth-600 font-semibold mb-5">
              Reserve Your Stay
            </p>
            <h2 className="font-serif font-light text-forest-900 text-3xl sm:text-5xl leading-tight mb-6 text-balance">
              Begin your journey to Mecana
            </h2>
            <p className="text-forest-500 text-base sm:text-lg leading-relaxed mb-10">
              Send us your dates and we'll craft a stay around the season —
              whales, waterfalls, mangroves and slow days by the sea.
            </p>

            <div className="space-y-5 mb-10">
              {contactItems.map((c) => {
                const content = (
                  <div className="flex items-center gap-4">
                    <span className="flex items-center justify-center w-11 h-11 rounded-full bg-forest-800 text-sand-100 shrink-0">
                      <c.icon size={18} />
                    </span>
                    <span className="text-forest-800 text-sm sm:text-base">
                      {c.label}
                    </span>
                  </div>
                );
                return c.href ? (
                  <a
                    key={c.label}
                    href={c.href}
                    target={c.href.startsWith("http") ? "_blank" : undefined}
                    rel="noreferrer"
                    data-testid={`contact-info-${c.key}`}
                    className="block hover:opacity-70 transition-opacity"
                  >
                    {content}
                  </a>
                ) : (
                  <div key={c.label} data-testid="contact-info-location">{content}</div>
                );
              })}
            </div>

            <a
              href={`https://wa.me/${HOTEL.phoneDigits}`}
              target="_blank"
              rel="noreferrer"
              data-testid="contact-whatsapp-button"
              className="inline-flex items-center gap-2 rounded-full bg-forest-800 hover:bg-forest-900 text-sand-100 px-7 py-3.5 text-sm font-semibold transition-colors duration-300"
            >
              <MessageCircle size={18} /> Chat on WhatsApp
            </a>
          </motion.div>

          <motion.form
            onSubmit={handleSubmit}
            data-testid="booking-form"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-white/60 backdrop-blur border border-forest-900/10 rounded-3xl p-7 sm:p-10"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <Field label="Full name" testid="form-name">
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  data-testid="input-name"
                  className="form-input"
                  placeholder="Your name"
                />
              </Field>
              <Field label="Email" testid="form-email">
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  data-testid="input-email"
                  className="form-input"
                  placeholder="you@email.com"
                />
              </Field>
              <Field label="Phone / WhatsApp" testid="form-phone">
                <input
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  data-testid="input-phone"
                  className="form-input"
                  placeholder="+57 ..."
                />
              </Field>
              <Field label="Guests" testid="form-guests">
                <select
                  value={form.guests}
                  onChange={update("guests")}
                  data-testid="input-guests"
                  className="form-input"
                >
                  {["1", "2", "3", "4", "5+"].map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </Field>
              <Field label="Check-in" testid="form-checkin">
                <input
                  type="date"
                  value={form.checkIn}
                  onChange={update("checkIn")}
                  data-testid="input-checkin"
                  className="form-input"
                />
              </Field>
              <Field label="Check-out" testid="form-checkout">
                <input
                  type="date"
                  value={form.checkOut}
                  onChange={update("checkOut")}
                  data-testid="input-checkout"
                  className="form-input"
                />
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Message" testid="form-message">
                <textarea
                  rows={4}
                  value={form.message}
                  onChange={update("message")}
                  data-testid="input-message"
                  className="form-input resize-none"
                  placeholder="Tell us what you'd love to experience..."
                />
              </Field>
            </div>
            <button
              type="submit"
              disabled={submitting}
              data-testid="submit-booking-button"
              className="mt-7 w-full inline-flex items-center justify-center gap-2 rounded-full bg-earth-500 hover:bg-earth-600 disabled:opacity-60 text-white px-8 py-4 text-sm font-semibold tracking-wide transition-colors duration-300"
            >
              {submitting ? (
                <>
                  <Loader2 size={18} className="animate-spin" /> Sending...
                </>
              ) : (
                "Send Request"
              )}
            </button>
          </motion.form>
        </div>
      </div>

      <style>{`
        .form-input {
          width: 100%;
          background: #EBE5DA;
          border: 1px solid transparent;
          border-radius: 0.85rem;
          padding: 0.75rem 1rem;
          font-size: 0.9rem;
          color: #1B3B2B;
          outline: none;
          transition: border-color .25s, background .25s;
        }
        .form-input::placeholder { color: #7A8A80; }
        .form-input:focus {
          border-color: #2B5E45;
          background: #F4F0E8;
        }
      `}</style>
    </section>
  );
};

const Field = ({ label, testid, children }) => (
  <label className="block" data-testid={testid}>
    <span className="block text-xs font-semibold tracking-wide uppercase text-forest-700/70 mb-2">
      {label}
    </span>
    {children}
  </label>
);
